import type { ActivityDto } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import { listTime, sinceLabel } from '@dbrain/app-core/when';

/**
 * Where a conversation stands, as the lists show it (D-78, brought forward
 * from Phase 14; docs/design/mockups/CalismaAlanlari.dc.html): a question
 * waits for the user's answer, an approval for their decision, or a run
 * goes on. An amber dot or badge for what waits, a blue spinner for what runs.
 */
export type ConversationStatus = 'answer' | 'approval' | 'running';

export function statusOf(activity: ActivityDto | undefined, conversationId: string): ConversationStatus | null {
  if (!activity) return null;
  const waiting = activity.pendingApprovals.filter((a) => a.conversationId === conversationId);
  if (waiting.some((a) => a.kind === 'question')) return 'answer';
  if (waiting.length > 0) return 'approval';
  return activity.activeRuns.some((r) => r.conversationId === conversationId) ? 'running' : null;
}

/** A small mark at the end of a row: a dot for what waits, a spinner for what runs. */
export function StatusDot({ status }: { status: ConversationStatus | null }) {
  const { t } = useTranslation();
  if (!status) return null;
  const label = t(`status.${status}`);
  return (
    <span className={status === 'running' ? 'status-spinner' : 'status-dot'} role="img" aria-label={label} title={label} data-testid="status-dot" data-status={status} />
  );
}

/** The same in words, for wider rows. */
export function StatusBadge({ status }: { status: ConversationStatus | null }) {
  const { t } = useTranslation();
  if (!status) return null;
  return (
    <span className={`status-badge status-badge--${status === 'running' ? 'running' : 'waiting'}`} data-testid="status-badge" data-status={status}>
      {t(`status.${status}`)}
    </span>
  );
}

/** A chat or a code conversation, as the rows' leading icon. */
export function ProfileIcon({ profile, size = 16 }: { profile: 'chat' | 'code'; size?: number }) {
  return profile === 'code' ? <Icons.code size={size} className="profile-icon profile-icon--code" /> : <Icons.chat size={size} className="profile-icon" />;
}

/** Times for rows and cards in the user's language. */
export function useTimes() {
  const { t, i18n } = useTranslation();
  return {
    list: (iso: string) => listTime(iso, i18n.language, { yesterday: t('time.yesterday') }),
    since: (iso: string) =>
      sinceLabel(iso, i18n.language, {
        now: t('time.now'),
        minutes: (count) => t('time.minutes', { count }),
        hours: (count) => t('time.hours', { count }),
        today: (time) => t('time.today', { time }),
        yesterday: t('time.yesterday'),
      }),
  };
}
